import { fromSavedData, toBoxSavedData, toCylinderSavedData, toSphereSavedData, } from "../../site/solid-mesh/solid-mesh-data";
import { SolidMeshConstructionHandler } from "./solid-mesh-construction-handler";
import { SolidMeshEditHandler } from "./solid-mesh-edit-handler";
import { Cartesian3, Cartographic, Math as CesiumMath, Color, HeadingPitchRoll, Matrix4, Quaternion, Transforms, } from "cesium";
import { buildPipeFromTwoPoints, buildRetainingWallFromTwoPoints, } from "../../site/solid-mesh/simple-structure-geometry";
import { buildSolidMeshWithBuilder } from "../../site/solid-mesh/solid-mesh-builder";
import { applyTransformToPositions, axisHorizontalBearingDegrees, computeLocalAxisEndpoint, extractShapeGeometry, quaternionToClosestDegrees, } from "../../site/solid-mesh/solid-mesh-geometry";
import { enforceAnchorConstraint } from "../../site/solid-mesh/solid-mesh-helper";
import { ViewerCamera } from "../../viewers/viewer-camera";
/**
 * Handler for managing solid mesh operations
 */
export class SolidMeshHandler {
    _constructionHandler;
    _editHandler;
    _site;
    _cesiumViewer;
    get editHandler() {
        return this._editHandler;
    }
    get constructionHandler() {
        return this._constructionHandler;
    }
    constructor(args, cesiumViewer) {
        this._site = args.site;
        this._cesiumViewer = cesiumViewer;
        this._constructionHandler = new SolidMeshConstructionHandler(args, cesiumViewer);
        this._editHandler = new SolidMeshEditHandler(args, cesiumViewer, args.site);
    }
    /**
     * Starts solid mesh construction
     * @param callback - Callback for construction events
     * @param shapeType - Shape type to construct
     * @param shapeFactory - Optional factory to compute initial shape from anchor position
     */
    startSolidMeshConstruction(callback, shapeType, shapeFactory) {
        this._constructionHandler.start(callback, shapeType, shapeFactory);
    }
    /**
     * Starts multi-point solid mesh construction.
     * onProgress is called after each click with { step, localPosition }.
     * onFinish is called with (solidMeshIds, { positions }) when all points are collected.
     * Right-click undoes the last picked point.
     *
     * @param callback - Callback for construction events
     * @param shapeType - Shape type to construct
     * @param maxPoints - Number of points to collect
     * @param shapeFactory - Optional factory to compute initial shape from all picked positions
     */
    startMultiPointSolidMeshConstruction(callback, shapeType, maxPoints, shapeFactory) {
        this._constructionHandler.startMultiPoint(callback, shapeType, maxPoints, shapeFactory);
    }
    /**
     * Undo last picked point during multi-point solid mesh construction.
     * Exposed so UI (context menu) can call it explicitly rather than
     * relying on raw right-click behaviour.
     */
    undoLastPickedPoint() {
        this._constructionHandler.undoLastPickedPoint();
    }
    /**
     * Return the number of points currently picked in the multi-point
     * solid-mesh construction flow.
     */
    getPickedPointCount() {
        return this._constructionHandler.getPickedPointCount();
    }
    /**
     * Finishes solid mesh construction
     */
    finishSolidMeshConstruction() {
        this._constructionHandler.finish();
    }
    /**
     * Cancels solid mesh construction
     */
    cancelSolidMeshConstruction() {
        this._constructionHandler.cancel();
    }
    /**
     * Checks if solid mesh is under construction
     * @returns True if under construction
     */
    checkSolidMeshUnderConstruction() {
        return this._constructionHandler.isUnderConstruction();
    }
    /**
     * Sets shape type during construction
     * @param shapeType - Shape type to set
     */
    setSolidMeshConstructionShapeType(shapeType) {
        this._constructionHandler.setShapeType(shapeType);
    }
    /**
     * Starts solid mesh editing
     * @param solidMesh - Solid mesh to edit
     * @param callback - Callback for edit events
     */
    async startSolidMeshEdit(solidMesh, callback) {
        this._editHandler.start(solidMesh, callback);
    }
    /**
     * Finishes solid mesh editing
     */
    async finishSolidMeshEdit() {
        await this._editHandler.end();
    }
    /**
     * Cancels solid mesh editing
     */
    cancelSolidMeshEdit() {
        this._editHandler.cancel();
    }
    /**
     * Checks if solid mesh is under edit
     * @returns True if under edit
     */
    isSolidMeshUnderEdit() {
        return this._editHandler.solidMeshUnderEdit !== undefined;
    }
    /**
     * Deletes a solid mesh
     * @param solidMeshId - ID of solid mesh to delete
     */
    deleteSolidMesh(solidMeshId) {
        try {
            const solidMesh = this._site.findSolidMesh(solidMeshId);
            if (solidMesh) {
                if (this._editHandler.solidMeshUnderEdit?.id === solidMeshId) {
                    this._editHandler.cancel();
                }
                this._site.deleteSolidMesh(solidMeshId);
            }
        }
        catch (error) {
            console.error("Error deleting solid mesh:", error);
            throw error;
        }
    }
    /**
     * Sets solid mesh visibility
     * @param solidMeshId - ID of solid mesh
     * @param visible - Visibility state
     */
    setSolidMeshVisibility(solidMeshId, visible) {
        try {
            const solidMesh = this._site.findSolidMesh(solidMeshId);
            if (solidMesh) {
                if (this._editHandler.solidMeshUnderEdit?.id === solidMeshId) {
                    this._editHandler.setVisibility(visible);
                }
                solidMesh.setVisibility(visible);
            }
        }
        catch (error) {
            console.error("Error setting solid mesh visibility:", error);
            throw error;
        }
    }
    /**
     * Flies camera to fit solid mesh in view
     * @param solidMeshId - ID of solid mesh
     */
    fitToSolidMesh(solidMeshId) {
        try {
            const solidMesh = this._site.findSolidMesh(solidMeshId);
            if (!solidMesh)
                return;
            const config = solidMesh.shapeConfig;
            const center = config.wgs84Center;
            const camera = ViewerCamera.getInstance(this._cesiumViewer.viewer);
            const transform = Transforms.eastNorthUpToFixedFrame(center);
            const offsetHeight = Cartographic.fromCartesian(center).height * 1.5;
            const localOffset = new Cartesian3(0.0, 0.0, offsetHeight);
            const offset = Matrix4.multiplyByPoint(transform, localOffset, new Cartesian3());
            camera.rawCamera.flyTo({
                destination: offset,
                orientation: {
                    heading: CesiumMath.toRadians(0),
                    pitch: CesiumMath.toRadians(-90),
                    roll: 0,
                },
            });
        }
        catch (error) {
            console.error("Error fitting to solid mesh:", error);
            throw error;
        }
    }
    /**
     * Rotates solid mesh under edit
     * @param angles - Rotation angles [roll(X), pitch(Y), heading(Z)] in degrees
     */
    rotateSolidMesh(angles) {
        this._editHandler.clearDisplayRotationDegrees();
        const headingRad = CesiumMath.toRadians(angles[2]);
        const pitchRad = CesiumMath.toRadians(angles[1]);
        const rollRad = CesiumMath.toRadians(angles[0]);
        const hpr = new HeadingPitchRoll(headingRad, pitchRad, rollRad);
        const quat = Quaternion.fromHeadingPitchRoll(hpr);
        this._editHandler.rotateSolidMesh(quat);
    }
    /**
     * Rotates solid mesh relative to its baseline orientation.
     * `delta` is converted to a quaternion and composed with baseline quaternion.
     * @param solidMeshId - Solid mesh ID (used to look up baseline)
     * @param delta - Delta rotation [roll, pitch, heading] in degrees
     */
    rotateSolidMeshFromBaseline(solidMeshId, delta) {
        if (this._editHandler.solidMeshUnderEdit?.id !== solidMeshId)
            return;
        const solidMesh = this._site.findSolidMesh(solidMeshId);
        if (!solidMesh)
            return;
        const deltaQuat = Quaternion.fromHeadingPitchRoll(new HeadingPitchRoll(CesiumMath.toRadians(delta[2]), CesiumMath.toRadians(delta[1]), CesiumMath.toRadians(delta[0])));
        const composedQuat = Quaternion.multiply(solidMesh.baselineQuaternion, deltaQuat, new Quaternion());
        this._editHandler.rotateSolidMesh(composedQuat);
        this._editHandler.setDisplayRotationChangeValue({
            x: delta[0],
            y: delta[1],
            z: delta[2],
        }, solidMeshId);
    }
    /**
     * Scales solid mesh under edit
     * @param scale - Scale values [x, y, z]
     */
    scaleSolidMesh(scale) {
        const scaleVec = new Cartesian3(scale[0], scale[1], scale[2]);
        this._editHandler.scaleSolidMesh(scaleVec);
    }
    /**
     * Sets solid mesh color
     * @param solidMeshId - ID of solid mesh
     * @param color - Color values [r, g, b] (0-1)
     */
    setSolidMeshColor(solidMeshId, color) {
        const solidMesh = this._site.findSolidMesh(solidMeshId);
        if (solidMesh) {
            const currentAlpha = solidMesh.shapeConfig.color?.alpha ?? 1.0;
            const cesiumColor = new Color(color[0], color[1], color[2], currentAlpha);
            solidMesh.setColor(cesiumColor);
            this._cesiumViewer.viewer.scene.requestRender();
        }
    }
    /**
     * Sets solid mesh transparency
     * @param solidMeshId - ID of solid mesh
     * @param transparency - Transparency value (0-1)
     */
    setSolidMeshTransparency(solidMeshId, transparency) {
        const solidMesh = this._site.findSolidMesh(solidMeshId);
        if (solidMesh) {
            solidMesh.setTransparency(transparency);
            this._cesiumViewer.viewer.scene.requestRender();
        }
    }
    /**
     * Sets anchor type on a solid mesh by ID.
     * If the mesh is currently under edit, delegates to EditHandler
     * (which also refreshes anchor reference). Otherwise updates directly.
     * When preservePosition is true, adjusts translation so the mesh stays
     * at the same world position after anchor change.
     */
    async setAnchorType(solidMeshId, anchorType, options) {
        if (options?.preservePosition) {
            const solidMesh = this._site.findSolidMesh(solidMeshId);
            if (!solidMesh)
                return;
            // Capture current anchor position before the change
            const anchorLocalBefore = solidMesh.getAnchorLocal();
            // Apply the anchor type change
            if (this._editHandler.solidMeshUnderEdit?.id === solidMeshId) {
                this._editHandler.setAnchorType(anchorType);
            }
            else {
                solidMesh.updateAttributes({ anchorType });
            }
            // The anchor point has moved in object space; compensate so the NEW
            // anchor sits exactly where the OLD anchor was
            const diff = Cartesian3.subtract(anchorLocalBefore, solidMesh.getAnchorLocal(), new Cartesian3());
            if (Cartesian3.magnitudeSquared(diff) > 1e-20) {
                await solidMesh.translateToLocalAnchor({
                    targetLocalAnchor: anchorLocalBefore,
                });
            }
            this._cesiumViewer.viewer.scene.requestRender();
            return;
        }
        if (this._editHandler.solidMeshUnderEdit?.id === solidMeshId) {
            this._editHandler.setAnchorType(anchorType);
            return;
        }
        const solidMesh = this._site.findSolidMesh(solidMeshId);
        if (solidMesh) {
            solidMesh.updateAttributes({ anchorType });
        }
    }
    /**
     * Sets solid mesh position in local coordinates.
     * @param solidMeshId - ID of solid mesh
     * @param localPosition - Local position (anchor based on anchorType)
     */
    async setLocalPosition(solidMeshId, localPosition) {
        const solidMesh = this._site.findSolidMesh(solidMeshId);
        if (!solidMesh)
            return;
        await solidMesh.translateToLocalAnchor({
            targetLocalAnchor: localPosition,
        });
        // Update helpers if editing this mesh
        if (this._editHandler.solidMeshUnderEdit?.id === solidMeshId) {
            const pivot = solidMesh.getAnchorWgs84();
            const quaternion = solidMesh.shapeConfig.quaternion;
            this._cesiumViewer.preview.axisHelper.update(pivot, 15, quaternion);
            this._cesiumViewer.preview.aabbHelper.update(solidMesh.shapeConfig.dimensions, solidMesh.modelMatrix);
        }
        this._cesiumViewer.viewer.scene.requestRender();
    }
    /**
     * Gets rotation angles in degrees
     * @param solidMeshId - ID of solid mesh
     * @param options.reference - Reference angles to find closest values to (optional)
     * @param options.relativeToBaseline - If true, returns rotation relative to baseline (creation/load-time)
     * @returns Rotation angles for each axis in degrees
     */
    getRotationDegrees(solidMeshId, options) {
        try {
            const solidMesh = this._site.findSolidMesh(solidMeshId);
            if (solidMesh) {
                const quaternion = solidMesh.shapeConfig.quaternion;
                if (quaternion) {
                    if (options?.relativeToBaseline) {
                        if (this._editHandler.displayRotationDegrees?.solidMeshId ===
                            solidMeshId) {
                            return {
                                x: this._editHandler.displayRotationDegrees.rotation.x,
                                y: this._editHandler.displayRotationDegrees.rotation.y,
                                z: this._editHandler.displayRotationDegrees.rotation.z,
                            };
                        }
                        const invBaseQuat = Quaternion.inverse(solidMesh.baselineQuaternion, new Quaternion());
                        const relativeQuat = Quaternion.multiply(invBaseQuat, quaternion, new Quaternion());
                        return quaternionToClosestDegrees(relativeQuat, options?.reference);
                    }
                    return quaternionToClosestDegrees(quaternion, options?.reference);
                }
            }
            return { x: 0, y: 0, z: 0 };
        }
        catch (error) {
            console.error("Error getting rotation degrees:", error);
            return { x: 0, y: 0, z: 0 };
        }
    }
    /**
     * Resets rotation to baseline (creation/load-time orientation).
     * @param solidMeshId - ID of solid mesh
     */
    resetRotationToBaseline(solidMeshId) {
        const solidMesh = this._site.findSolidMesh(solidMeshId);
        if (!solidMesh)
            return;
        solidMesh.resetToBaselineRotation();
        this._editHandler.setDisplayRotationChangeValue({ x: 0, y: 0, z: 0 }, solidMeshId);
        this._cesiumViewer.viewer.scene.requestRender();
    }
    /**
     * Gets solid mesh anchor point in local coordinates
     * @param solidMeshId - ID of solid mesh
     * @returns Local anchor position
     */
    async getAnchorLocal(solidMeshId) {
        return await this._site.getSolidMesh(solidMeshId).resolveAnchorLocal();
    }
    /**
     * Applies preview-only transform in-place (no primitive dispose+rebuild).
     * Does NOT require edit mode. Reuses the existing primitive.
     * @param solidMeshId - ID of solid mesh
     * @param options.dimensions - Target dimensions; converted to scale internally
     * @param options.scale - Absolute scale {x,y,z} (ignored if dimensions provided)
     * @param options.localCenter - New local-space center {x,y,z}
     * @param options.hprRad - Optional HPR rotation in radians {heading,pitch,roll}
     */
    async applyPreviewParams(solidMeshId, options) {
        const solidMesh = this._site.getSolidMesh(solidMeshId);
        // Apply rotation (absolute)
        if (options.hprRad) {
            const { heading, pitch, roll } = options.hprRad;
            const hpr = new HeadingPitchRoll(heading, pitch, roll);
            solidMesh.rotate(Quaternion.fromHeadingPitchRoll(hpr));
        }
        // Apply scale (absolute) — dimensions takes precedence over raw scale
        const effectiveScale = options.dimensions
            ? (() => {
                const { x, y, z } = solidMesh.shapeConfig.dimensions;
                if (!x || !y || !z) {
                    throw new Error("Cannot compute scale ratio: current mesh has zero dimension");
                }
                return {
                    x: options.dimensions.x / x,
                    y: options.dimensions.y / y,
                    z: options.dimensions.z / z,
                };
            })()
            : options.scale;
        if (effectiveScale) {
            solidMesh.scale(new Cartesian3(effectiveScale.x, effectiveScale.y, effectiveScale.z));
        }
        // Move to the desired localCenter; rebuilds the local frame there
        if (options.localCenter) {
            const { quaternion, scale } = solidMesh.shapeConfig;
            await solidMesh.applyLocalTargetState({
                localCenter: new Cartesian3(options.localCenter.x, options.localCenter.y, options.localCenter.z),
                quaternion,
                scale,
            });
        }
        enforceAnchorConstraint(solidMesh);
    }
    /**
     * Applies a computed TGeometryResult to a mesh as one atomic target state:
     * dimensions→scale, HPR→quaternion, center, and meta (anchorType/bottomZ).
     * No post-hoc anchor correction runs — constraints are already baked into
     * the computed center by the geometry builders.
     */
    async applyGeometryResultToMesh(solidMesh, geometry, scaleYPolicy) {
        const baseDims = solidMesh.shapeConfig.dimensions;
        if (!baseDims.x || !baseDims.y || !baseDims.z) {
            throw new Error("Cannot compute scale ratio: current mesh has zero dimension");
        }
        const quaternion = Quaternion.fromHeadingPitchRoll(new HeadingPitchRoll(geometry.rotation.z, // heading
        geometry.rotation.y, // pitch
        geometry.rotation.x));
        const scale = new Cartesian3(geometry.dimensions.x / baseDims.x, scaleYPolicy === "preserve-current"
            ? solidMesh.shapeConfig.scale.y
            : geometry.dimensions.y / baseDims.y, geometry.dimensions.z / baseDims.z);
        await solidMesh.applyLocalTargetState({
            localCenter: new Cartesian3(geometry.center.x, geometry.center.y, geometry.center.z),
            quaternion,
            scale,
        });
        solidMesh.updateAttributes({
            anchorType: geometry.meta.anchorType,
            ...(geometry.meta.bottomZ !== undefined
                ? { bottomZ: geometry.meta.bottomZ }
                : {}),
        });
        // Absolute rotation set → invalidate this mesh's relative display-rotation cache
        this._editHandler.clearDisplayRotationDegrees(solidMesh.id);
        if (this._editHandler.solidMeshUnderEdit?.id === solidMesh.id) {
            this._editHandler.refreshEditHelpers();
        }
        this._cesiumViewer.viewer.scene.requestRender();
    }
    /**
     * Atomically updates a retaining wall from its two top-edge basis points.
     * Computes the full target state (center/rotation/scale/bottomZ) up front
     * via `buildRetainingWallFromTwoPoints` and applies it in one shot, so the
     * same input always yields the same state (deterministic and reversible).
     * @returns The geometry applied to the mesh
     */
    async updateRetainingWallFromTwoPoints(solidMeshId, params) {
        const solidMesh = this._site.getSolidMesh(solidMeshId);
        const dx = params.p2.x - params.p1.x;
        const dy = params.p2.y - params.p1.y;
        if (Math.sqrt(dx * dx + dy * dy) < 1e-8) {
            throw new Error("Retaining wall basis points P1 and P2 must not coincide horizontally");
        }
        if (!(params.heightM > 0)) {
            throw new Error("Retaining wall height must be positive");
        }
        // Keep the current rendered thickness (build's center offset depends on it)
        const currentScaleY = solidMesh.shapeConfig.scale.y;
        const thicknessM = solidMesh.shapeConfig.dimensions.y * Math.abs(currentScaleY);
        const geometry = buildRetainingWallFromTwoPoints({
            P1: params.p1,
            P2: params.p2,
            height_m: params.heightM,
            gradient_percent: params.gradientPercent,
            thickness_m: thicknessM,
        });
        await this.applyGeometryResultToMesh(solidMesh, geometry, "preserve-current");
        return geometry;
    }
    /**
     * Atomically updates a pipe from its two axis endpoints.
     * Same contract as `updateRetainingWallFromTwoPoints`: the full target
     * state is computed from the input alone via `buildPipeFromTwoPoints`
     * and applied in one shot — deterministic and reversible.
     * @returns The geometry applied to the mesh
     */
    async updatePipeFromTwoPoints(solidMeshId, params) {
        const solidMesh = this._site.getSolidMesh(solidMeshId);
        const dx = params.p2.x - params.p1.x;
        const dy = params.p2.y - params.p1.y;
        const dz = params.p2.z - params.p1.z;
        const lengthM = Math.sqrt(dx * dx + dy * dy + dz * dz);
        if (lengthM < 1e-8) {
            throw new Error("Pipe endpoints P1 and P2 must not coincide");
        }
        if (!(params.diameterMm > 0)) {
            throw new Error("Pipe diameter must be positive");
        }
        const geometry = buildPipeFromTwoPoints({
            P1: params.p1,
            P2: params.p2,
            diameter_mm: params.diameterMm,
        });
        await this.applyGeometryResultToMesh(solidMesh, geometry, "from-geometry");
        return geometry;
    }
    /**
     * Reconstructs the two basis points of a two-point structure from the
     * authoritative quaternion (P2 = P1 + rotated-axis · extent) plus the
     * baseline-relative horizontal bearing for the azimuth display.
     * Never decomposes quaternions into Euler angles, so it stays correct
     * for steep and downward pipes (no gimbal-lock heading flips).
     * @param axis - Object-local extension axis: "z" for pipe, "x" for retaining-wall
     */
    async getSolidMeshLocalEndpoints(solidMeshId, axis) {
        const solidMesh = this._site.getSolidMesh(solidMeshId);
        const p1 = await this.getAnchorLocal(solidMeshId);
        const quaternion = Quaternion.normalize(solidMesh.shapeConfig.quaternion, new Quaternion());
        const p2 = computeLocalAxisEndpoint(p1, quaternion, solidMesh.shapeConfig.scale, solidMesh.shapeConfig.dimensions, axis);
        // Invariant: two-point structures always carry a positive axis scale —
        // every creation/edit path derives scale from positive dimensions, so
        // the quaternion axis IS the physical P1→P2 direction on both sides of
        // this delta (no scale-sign compensation needed).
        const currentBearing = axisHorizontalBearingDegrees(quaternion, axis);
        const baselineBearing = axisHorizontalBearingDegrees(solidMesh.baselineQuaternion, axis);
        // Normalize to [-180, 180)
        const relativeBearingDeg = currentBearing !== null && baselineBearing !== null
            ? ((((currentBearing - baselineBearing + 180) % 360) + 360) % 360) - 180
            : null;
        return {
            p1: { x: p1.x, y: p1.y, z: p1.z },
            p2: { x: p2.x, y: p2.y, z: p2.z },
            relativeBearingDeg,
        };
    }
    /**
     * Gets solid mesh SavedData (shapeConfig + attributes)
     * @param solidMeshId - ID of solid mesh
     * @returns TSolidMeshSavedData (Plain JSON format)
     */
    getSolidMeshJSON(solidMeshId) {
        const solidMesh = this._site.getSolidMesh(solidMeshId);
        const { id, shapeConfig, attributes } = solidMesh.toJson();
        const baselineQuaternion = solidMesh.baselineQuaternion;
        const baselineQuaternionSavedData = {
            x: baselineQuaternion.x,
            y: baselineQuaternion.y,
            z: baselineQuaternion.z,
            w: baselineQuaternion.w,
        };
        switch (shapeConfig.shapeType) {
            case "box":
                return toBoxSavedData(id, shapeConfig, attributes, {
                    baselineQuaternion: baselineQuaternionSavedData,
                });
            case "sphere":
                return toSphereSavedData(id, shapeConfig, attributes, {
                    baselineQuaternion: baselineQuaternionSavedData,
                });
            case "cylinder":
                return toCylinderSavedData(id, shapeConfig, attributes, {
                    baselineQuaternion: baselineQuaternionSavedData,
                });
            default:
                throw new Error(shapeConfig);
        }
    }
    /**
     * Loads solid mesh from SavedData (validated by Client)
     * @param data - TSolidMeshSavedData (validated, shapeType + shapeConfig + attributes)
     * @returns { id: string }
     */
    async loadSolidMeshJSON(data) {
        // Convert attributes to builder format (id passed separately)
        // isVisible is not stored - managed via primitive.show
        // Pass through optional viewer-only metadata such as bottomZ so that
        // SolidMesh attributes retain adapter-provided values for editing.
        const builderAttributes = {
            anchorType: data.attributes.anchorType,
            ...(data.attributes?.bottomZ !== undefined
                ? { bottomZ: data.attributes.bottomZ }
                : {}),
        };
        const visibleFlag = data.attributes?.visible;
        // NOTE: Do not pre-delete existing meshes here. Deletion/replace logic is
        // centralized in `Site.addSolidMesh` to avoid double-dispose / race
        // conditions when construction and registration overlap.
        // Convert Plain SavedData to Cesium TShapeConfig and build
        const shapeConfig = fromSavedData(data);
        const baselineQuaternion = data.attributes?.baselineQuaternion
            ? new Quaternion(data.attributes.baselineQuaternion.x, data.attributes.baselineQuaternion.y, data.attributes.baselineQuaternion.z, data.attributes.baselineQuaternion.w)
            : undefined;
        const solidMesh = await buildSolidMeshWithBuilder({
            cesiumViewer: this._cesiumViewer,
            shapeConfig: shapeConfig,
            crsConverter: this._site.crsConverter,
            id: data.id,
            attributes: builderAttributes,
            baselineQuaternion,
            visible: visibleFlag,
        });
        if (!solidMesh) {
            throw new Error("Failed to create solid mesh from data");
        }
        this._site.addSolidMesh(solidMesh);
        return { id: solidMesh.id };
    }
    /**
     * Gets solid mesh geometry data
     * @param solidMeshId - ID of solid mesh
     * @returns Vertices and indices arrays
     */
    getSolidMeshGeometry(solidMeshId) {
        try {
            const solidMesh = this._site.findSolidMesh(solidMeshId);
            if (solidMesh) {
                const indexedGeometry = extractShapeGeometry(solidMesh.shapeConfig);
                const transformedIndxedGeometry = applyTransformToPositions(indexedGeometry);
                return {
                    vertices: transformedIndxedGeometry.vertices,
                    indices: transformedIndxedGeometry.indices,
                };
            }
            return { vertices: [], indices: [] };
        }
        catch (error) {
            console.error("Error getting solid mesh geometry:", error);
            return { vertices: [], indices: [] };
        }
    }
}
//# sourceMappingURL=solid-mesh-handler.js.map